import { useState } from "react";
import { Link } from "react-router-dom";
import { SnackbarNotice } from "@/shared/notifications/SnackbarNotice";
import { Button, TextField, Typography } from "@mui/material";
import { confirmResetPassword, resetPassword } from "./auth.api";
import {
  AuthCard,
  AuthPasswordField,
  authFeedbackSx,
  authFieldSx,
  authLinkSx,
  authMutedTextSx,
  authPrimaryActionSx,
} from "./components";
import { authErrorCopy, isPrivateResetFailure, RESET_REQUEST_COPY, RESET_RETRY_COPY } from "./auth-copy";

export const ResetPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<"request" | "confirm" | "success">("request");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await resetPassword({ username: email });
      void result;
      setNotice(RESET_REQUEST_COPY);
      setStep("confirm");
    } catch (cause: unknown) {
      if (isPrivateResetFailure(cause)) {
        setNotice(RESET_REQUEST_COPY);
        setStep("confirm");
      } else {
        setError(RESET_RETRY_COPY);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword: password });
      setPassword("");
      setCode("");
      setStep("success");
    } catch (cause) {
      setError(authErrorCopy(cause));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === "success") {
    return (
      <AuthCard title="PASSWORD UPDATED" detail="Your new password is ready. Continue securely to your account.">
        <Button component={Link} to="/login" fullWidth variant="contained" sx={authPrimaryActionSx}>
          CONTINUE TO LOG IN
        </Button>
      </AuthCard>
    );
  }

  if (step === "confirm") {
    return (
      <AuthCard title="SET A NEW PASSWORD" detail={<>We sent a reset code to {email}</>}>
        <form onSubmit={(event) => void confirmCode(event)}>
          <TextField
            autoComplete="one-time-code"
            fullWidth
            required
            label="Reset code"
            name="reset-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            sx={authFieldSx}
          />
          <AuthPasswordField
            autoComplete="new-password"
            fullWidth
            required
            label="New password"
            name="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            inputProps={{ minLength: 8 }}
          />
          {notice && (
            <SnackbarNotice severity="info" sx={authFeedbackSx}>
              {notice}
            </SnackbarNotice>
          )}
          {error && (
            <SnackbarNotice severity="error" sx={authFeedbackSx}>
              {error}
            </SnackbarNotice>
          )}
          <Button type="submit" variant="contained" fullWidth disabled={isSubmitting} sx={authPrimaryActionSx}>
            {isSubmitting ? "RESETTING PASSWORD…" : "RESET PASSWORD"}
          </Button>
          <Button
            type="button"
            onClick={() => {
              setStep("request");
              setCode("");
              setError(null);
              setNotice(null);
            }}
            sx={{ ...authLinkSx, minHeight: 44, mt: 1.5 }}
          >
            CHANGE EMAIL
          </Button>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="RESET YOUR PASSWORD" detail="Enter your email and we will send a reset code.">
      <form onSubmit={(event) => void requestCode(event)}>
        <TextField
          autoComplete="email"
          autoCapitalize="none"
          type="email"
          fullWidth
          required
          label="Email"
          name="email"
          spellCheck={false}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          sx={authFieldSx}
        />
        {error && (
          <SnackbarNotice severity="error" sx={authFeedbackSx}>
            {error}
          </SnackbarNotice>
        )}
        <Button type="submit" variant="contained" fullWidth disabled={isSubmitting} sx={authPrimaryActionSx}>
          {isSubmitting ? "SENDING CODE…" : "SEND RESET CODE"}
        </Button>
      </form>
      <Typography variant="caption" sx={{ ...authMutedTextSx, display: "block", mt: 3 }}>
        <Link to="/login" style={authLinkSx}>
          BACK TO LOG IN
        </Link>
      </Typography>
    </AuthCard>
  );
};
